import * as React from 'react';
import { FormState, CmFactor, GlobalPolygon } from '../types';
import { MAX_GLOBAL_POLYGONS } from '../lib/storage';

const { useEffect, useRef } = React;

interface Props {
  form: FormState;
  set: (patch: Partial<FormState>) => void;
  drawPolygonMode?: boolean;
  editingPolygonId?: string | null;
  onStartDrawPolygon?: () => void;
  onEditPolygon?: (id: string) => void;
  onDeletePolygon?: (id: string) => void;
  onTogglePolygonActive?: (id: string) => void;
  onToggleAllPolygons?: (active: boolean) => void;
  onClearAllPolygons?: () => void;
  onSavePolygonGlobal?: (id: string) => void;
  globalPolygons?: GlobalPolygon[];
  onDeleteGlobalPolygon?: (id: string) => void;
  onAddGlobalToRoute?: (gp: GlobalPolygon) => void;
}

/**
 * Build the custom_model JSON string from the current UI controls.
 * Used to keep the raw JSON textarea in sync with UI edits.
 */
function buildJsonFromControls(form: FormState): string {
  const cm: any = {};
  const di = form.distInfluence.trim();
  if (di && !isNaN(Number(di))) cm.distance_influence = Number(di);

  const priorityFromFactors = (form.cmFactors || [])
    .filter(r => r.type === 'priority' && r.condition.trim() && r.multiplyBy.trim())
    .map(r => {
      const key = r.conditionType === 'else_if' ? 'else_if' : r.conditionType === 'else' ? 'else' : 'if';
      return { [key]: r.condition.trim(), multiply_by: parseFloat(r.multiplyBy) };
    });
  let priorityFromJson: any[] = [];
  if (form.priorityJson.trim()) {
    try { priorityFromJson = JSON.parse(form.priorityJson); } catch { /* ignore */ }
  }
  const allPriority = [...priorityFromFactors, ...priorityFromJson];
  if (allPriority.length) cm.priority = allPriority;

  const speedFromFactors = (form.cmFactors || [])
    .filter(r => r.type === 'speed' && r.condition.trim() && r.multiplyBy.trim())
    .map(r => {
      const key = r.conditionType === 'else_if' ? 'else_if' : r.conditionType === 'else' ? 'else' : 'if';
      return { [key]: r.condition.trim(), limit_to: parseFloat(r.multiplyBy) };
    });
  let speedFromJson: any[] = [];
  if (form.speedJson.trim()) {
    try { speedFromJson = JSON.parse(form.speedJson); } catch { /* ignore */ }
  }
  const allSpeed = [...speedFromFactors, ...speedFromJson];
  if (allSpeed.length) cm.speed = allSpeed;

  return Object.keys(cm).length ? JSON.stringify(cm, null, 2) : '';
}

/**
 * Parse raw custom_model JSON back into UI control values (cmFactors, distInfluence, etc.)
 */
function parseJsonToControls(json: string): Partial<FormState> | null {
  try {
    const cm = JSON.parse(json);
    const patch: Partial<FormState> = {};
    patch.distInfluence = cm.distance_influence != null ? String(cm.distance_influence) : '';

    const factors: CmFactor[] = [];
    if (Array.isArray(cm.priority)) {
      for (const rule of cm.priority) {
        const condition = rule.if || rule.else_if || rule.else || '';
        const conditionType: 'if' | 'else_if' | 'else' = rule.if ? 'if' : rule.else_if ? 'else_if' : 'else';
        if (condition && rule.multiply_by != null) {
          factors.push({
            id: crypto.randomUUID(),
            condition,
            multiplyBy: String(rule.multiply_by),
            type: 'priority',
            conditionType,
          });
        }
      }
    }
    if (Array.isArray(cm.speed)) {
      for (const rule of cm.speed) {
        const condition = rule.if || rule.else_if || rule.else || '';
        const conditionType: 'if' | 'else_if' | 'else' = rule.if ? 'if' : rule.else_if ? 'else_if' : 'else';
        if (condition && rule.limit_to != null) {
          factors.push({
            id: crypto.randomUUID(),
            condition,
            multiplyBy: String(rule.limit_to),
            type: 'speed',
            conditionType,
          });
        }
      }
    }
    patch.cmFactors = factors;
    // Clear the raw advanced textareas since everything is now in cmFactors
    patch.priorityJson = '';
    patch.speedJson = '';
    return patch;
  } catch {
    return null;
  }
}

// Suggested conditions for the custom model factor UI dropdowns.
const CONDITION_PRESETS: [string, string][] = [
  ['road_class == MOTORWAY', 'Motorway / Autobahn'],
  ['road_class == PRIMARY', 'Primary roads'],
  ['road_class == SECONDARY', 'Secondary roads'],
  ['road_class == TERTIARY', 'Tertiary roads'],
  ['road_class == RESIDENTIAL', 'Residential roads'],
  ['road_class == SERVICE', 'Service roads'],
  ['road_class == TRUNK', 'Trunk roads'],
  ['road_class == UNCLASSIFIED', 'Unclassified roads'],
  ['road_environment == TUNNEL', 'Tunnels'],
  ['road_environment == BRIDGE', 'Bridges'],
  ['road_environment == FORD', 'Fords'],
  ['surface == UNPAVED', 'Unpaved surface'],
  ['surface == PAVED', 'Paved surface'],
  ['surface == GRAVEL', 'Gravel surface'],
  ['toll == ALL', 'All toll roads'],
  ['toll == HGV', 'HGV toll roads'],
  ['country == DEU', 'Germany'],
  ['country == CZE', 'Czech Republic'],
  ['country == AUT', 'Austria'],
  ['country == TUR', 'Turkey'],
  ['max_speed > 100', 'Speed limit > 100 km/h'],
  ['max_speed >= 80', 'Speed limit ≥ 80 km/h'],
  ['max_speed <= 50', 'Speed limit ≤ 50 km/h'],
  ['max_speed <= 30', 'Speed limit ≤ 30 km/h'],
  ['max_speed < 50', 'Speed limit < 50 km/h'],
];

const EXTRAS: [string, string][] = [
  ['tollways', 'Toll road segments and toll-distance summary.'],
  ['waycategory', 'Road category classification (motorway, primary, secondary, ...).'],
  ['surface', 'Surface type per segment (asphalt, gravel, paving, ...).'],
  ['waytype', 'Way type per segment (road, track, footway, ferry, ...).'],
  ['steepness', 'Steepness/gradient classification per segment. Requires elevation.'],
  ['suitability', 'Suitability score per segment for the chosen profile.'],
  ['roadaccessrestrictions', 'Road access restrictions warnings.'],
  ['countryinfo', 'Country codes each segment passes through.'],
  ['green', 'Green-score per segment.'],
  ['noise', 'Noise-score per segment.'],
];

const ATTRS: [string, string][] = [
  ['avgspeed', 'Average speed (km/h) for each segment step.'],
  ['detourfactor', 'Detour factor: ratio of segment distance to beeline distance.'],
  ['percentage', 'Percentage of total route distance each segment covers.'],
];

const AVOID: [string, string][] = [
  ['highways', 'Avoid motorways/highways entirely.'],
  ['tollways', 'Avoid toll roads (vignettes, péages, Maut).'],
  ['ferries', 'Avoid ferry crossings.'],
  ['fords', 'Avoid river fords.'],
  ['steps', 'Avoid steps/stairs.'],
];

function CheckGroup({
  items, values, onChange, cls = 'grid3',
}: {
  items: [string, string][];
  values: Record<string, boolean>;
  onChange: (key: string, v: boolean) => void;
  cls?: string;
}) {
  return (
    <div className={`${cls} checkboxes`}>
      {items.map(([k, tip]) => (
        <label key={k} title={tip}>
          <input
            type="checkbox"
            checked={!!values[k]}
            onChange={(e) => onChange(k, e.target.checked)}
          /> {k}
        </label>
      ))}
    </div>
  );
}

/**
 * Custom Model section — shows BOTH the UI controls and a raw JSON editor,
 * kept in sync bidirectionally:
 * - UI changes auto-update the JSON textarea
 * - Clicking the save icon on the JSON textarea applies it back to UI controls
 */
function CustomModelSection({ form, set }: Props) {
  const jsonRef = useRef<HTMLTextAreaElement>(null);
  // Track whether user is actively editing the JSON textarea
  const jsonEditingRef = useRef(false);

  // Auto-sync: UI controls → JSON (only when not manually editing JSON)
  const generatedJson = buildJsonFromControls(form);

  // Keep customModelOverrideJson in sync with controls (unless user is editing JSON directly)
  useEffect(() => {
    if (!jsonEditingRef.current && form.customModelOverrideJson !== generatedJson) {
      set({ customModelOverrideJson: generatedJson });
    }
  }, [generatedJson]);

  const handleJsonSave = () => {
    const parsed = parseJsonToControls(form.customModelOverrideJson);
    if (parsed) {
      set(parsed);
      jsonEditingRef.current = false;
    } else {
      alert('Invalid JSON — could not parse custom_model');
    }
  };

  return (
    <section>
      <h2>Custom Model</h2>

      {/* UI Controls */}
      <div className="row">
        <label>distance_influence <span className="tip" title="Seconds saved per km 'worth' a detour. 0=fastest, 100=mild, 200=strong, 1000≈shortest.">ⓘ</span></label>
        <input
          type="number" step={10} placeholder="e.g. 100"
          value={form.distInfluence}
          onChange={(e) => set({ distInfluence: e.target.value })}
        />
      </div>

      {/* Factor rows UI */}
      <div className="row" style={{ marginTop: 10 }}>
        <label>Priority / Speed factors <span className="tip" title="Add rules that boost/penalize roads. Priority: multiply_by > 1 = prefer, < 1 = avoid. Speed: value = limit_to km/h cap.">ⓘ</span></label>
        <div className="cm-factors">
          {(form.cmFactors || []).map((fac, idx) => (
            <div key={fac.id} className="cm-factor-row">
              <select
                value={fac.type}
                onChange={(e) => {
                  const copy = form.cmFactors.slice();
                  copy[idx] = { ...copy[idx], type: e.target.value as 'priority' | 'speed' };
                  set({ cmFactors: copy });
                }}
                title="priority = boost/penalize, speed = cap km/h"
              >
                <option value="priority">priority</option>
                <option value="speed">speed</option>
              </select>
              <select
                value={fac.conditionType || 'if'}
                onChange={(e) => {
                  const copy = form.cmFactors.slice();
                  copy[idx] = { ...copy[idx], conditionType: e.target.value as 'if' | 'else_if' | 'else' };
                  set({ cmFactors: copy });
                }}
                title="if = independent rule, else_if = chained (only applies when previous didn't match)"
                style={{ width: 72 }}
              >
                <option value="if">if</option>
                <option value="else_if">else_if</option>
                <option value="else">else</option>
              </select>
              <select
                value={CONDITION_PRESETS.some(([c]) => c === fac.condition) ? fac.condition : '__custom__'}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '__custom__') return;
                  const copy = form.cmFactors.slice();
                  copy[idx] = { ...copy[idx], condition: val };
                  set({ cmFactors: copy });
                }}
                title="Pick a preset condition or type a custom one"
              >
                {CONDITION_PRESETS.map(([c, lbl]) => (
                  <option key={c} value={c}>{lbl}</option>
                ))}
                <option value="__custom__">— custom —</option>
              </select>
              <input
                type="text"
                style={{ flex: 1, minWidth: 0 }}
                placeholder="condition"
                value={fac.condition}
                onChange={(e) => {
                  const copy = form.cmFactors.slice();
                  copy[idx] = { ...copy[idx], condition: e.target.value };
                  set({ cmFactors: copy });
                }}
                title='GraphHopper expression, e.g. "road_class == MOTORWAY"'
              />
              <input
                type="number"
                step={0.1}
                style={{ width: 64 }}
                placeholder={fac.type === 'speed' ? 'km/h' : '× factor'}
                value={fac.multiplyBy}
                onChange={(e) => {
                  const copy = form.cmFactors.slice();
                  copy[idx] = { ...copy[idx], multiplyBy: e.target.value };
                  set({ cmFactors: copy });
                }}
                title={fac.type === 'priority'
                  ? 'multiply_by: >1 = prefer, <1 = avoid (e.g. 0.9 penalizes, 1.2 boosts)'
                  : 'limit_to: speed cap in km/h'}
              />
              <button
                type="button"
                onClick={() => {
                  const copy = form.cmFactors.slice();
                  copy.splice(idx, 1);
                  set({ cmFactors: copy });
                }}
                title="Remove this rule"
              >✕</button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              set({
                cmFactors: [
                  ...(form.cmFactors || []),
                  { id: crypto.randomUUID(), condition: 'road_class == MOTORWAY', multiplyBy: '1.0', type: 'priority' },
                ],
              });
            }}
          >+ Add factor</button>
        </div>
      </div>

      {/* Fallback raw JSON textareas (still available for advanced users) */}
      <details style={{ marginTop: 10 }}>
        <summary style={{ fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>
          Advanced: raw priority / speed JSON (merged with factors above)
        </summary>
        <div className="row">
          <label>priority rules (JSON array)</label>
          <textarea
            rows={3}
            placeholder='[{"if":"road_class == MOTORWAY","multiply_by":1.2}]'
            value={form.priorityJson}
            onChange={(e) => set({ priorityJson: e.target.value })}
          />
        </div>
        <div className="row">
          <label>speed rules (JSON array)</label>
          <textarea
            rows={2}
            placeholder='[{"if":"road_class == RESIDENTIAL","limit_to":30}]'
            value={form.speedJson}
            onChange={(e) => set({ speedJson: e.target.value })}
          />
        </div>
      </details>

      {/* Raw JSON view — always visible, synced with controls */}
      <div className="row" style={{ marginTop: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          custom_model JSON
          <span className="tip" title="This JSON is auto-generated from the controls above. Edit it directly and click 💾 to apply changes back to the UI controls.">ⓘ</span>
          <button
            type="button"
            onClick={handleJsonSave}
            title="Apply JSON → fill UI controls"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}
          >💾</button>
        </label>
        <textarea
          ref={jsonRef}
          rows={8}
          style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }}
          placeholder={'{\n  "distance_influence": 180,\n  "priority": [\n    {"if": "road_class == MOTORWAY", "multiply_by": 0.9}\n  ]\n}'}
          value={form.customModelOverrideJson}
          onFocus={() => { jsonEditingRef.current = true; }}
          onBlur={() => { /* keep editing flag until save */ }}
          onChange={(e) => {
            jsonEditingRef.current = true;
            set({ customModelOverrideJson: e.target.value });
          }}
        />
        <p className="hint">Edit JSON directly then click 💾 to sync back to controls, or use controls above (auto-syncs to JSON).</p>
      </div>
    </section>
  );
}

export default function DirectionsOptions({ form, set, drawPolygonMode, editingPolygonId, onStartDrawPolygon, onEditPolygon, onDeletePolygon, onTogglePolygonActive, onToggleAllPolygons, onClearAllPolygons, onSavePolygonGlobal, globalPolygons = [], onDeleteGlobalPolygon, onAddGlobalToRoute }: Props) {
  const prefHint = (() => {
    switch (form.preference) {
      case 'custom':   return '⚠ Custom mode — recommended/shortest weighting is ignored. Custom model is used instead.';
      case 'shortest': return 'Pure shortest-distance route. Truck restrictions still apply.';
      case 'fastest':  return 'Pure fastest route by travel time.';
      default:         return 'Balanced ORS default. Considers safety, road class, time, distance.';
    }
  })();

  return (
    <>
      <section>
        <h2>Preference</h2>
        <select
          value={form.preference}
          onChange={(e) => set({ preference: e.target.value })}
          title="How ORS scores possible routes."
        >
          <option value="recommended">recommended</option>
          <option value="shortest">shortest</option>
          <option value="fastest">fastest</option>
          <option value="custom">custom (uses custom_model)</option>
        </select>
        <p className="hint">{prefHint}</p>

        <label className="checkbox" style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}
          title='Adds "optimized": false to the request, forcing ORS to skip Contraction Hierarchies (CH) and use a slower but more flexible algorithm. Required when CH is enabled server-side and the route triggers edge-case failures.'>
          <input
            type="checkbox"
            checked={!!form.disableCH}
            onChange={(e) => set({ disableCH: e.target.checked })}
          />
          Disable CH&nbsp;<span style={{ fontSize: 10, color: 'var(--muted)' }}>(optimized: false)</span>
          <span className="tip" title='Sends "optimized": false — disables Contraction Hierarchies for this request. Use when CH causes incorrect or failed routes. Requires disabling_allowed: true on the server.'>ⓘ</span>
        </label>
        {form.disableCH && (
          <p className="hint" style={{ color: 'var(--warn, #b8860b)', marginTop: 4 }}>
            ⚠ CH disabled — slower but uses full Dijkstra/A*. Ensure the server config has <code>disabling_allowed: true</code>.
          </p>
        )}
      </section>

      {form.preference === 'custom' && (
        <CustomModelSection form={form} set={set} />
      )}

      <section>
        <h2>Extra Info</h2>
        <CheckGroup
          items={EXTRAS}
          values={form.extras}
          onChange={(k, v) => set({ extras: { ...form.extras, [k]: v } })}
        />
      </section>

      <section>
        <h2>Attributes <small>(per-segment)</small></h2>
        <CheckGroup
          items={ATTRS}
          values={form.attributes}
          onChange={(k, v) => set({ attributes: { ...form.attributes, [k]: v } })}
        />
      </section>

      <section>
        <h2>Avoid Features</h2>
        <CheckGroup
          items={AVOID}
          values={form.avoid}
          onChange={(k, v) => set({ avoid: { ...form.avoid, [k]: v } })}
        />
      </section>

      <section>
        <h2>Maximum Speed <span className="tip" title="Set options.maximum_speed (km/h). Can force algorithm switch in ORS (Core-ALT workaround). E.g. 100.">ⓘ</span></h2>
        <input
          type="number"
          min={1}
          step={1}
          placeholder="e.g. 100 (km/h)"
          value={form.maximumSpeed}
          onChange={(e) => set({ maximumSpeed: e.target.value })}
          style={{ width: '100%' }}
        />
        <p className="hint">Limits route speed to this value (km/h). Can resolve Core-ALT routing issues.</p>
      </section>

      <section>
        <h2>
          Avoid Polygons
          <span className="tip" title="Draw areas the route should avoid. Double-click a polygon on the map to edit it.">ⓘ</span>
          {(form.avoidPolygons?.length > 0 || form.avoidPolygonsJson) && (
            <button
              type="button"
              onClick={onClearAllPolygons}
              title="Remove all avoid polygons from this route"
              style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', color: 'var(--err)', background: 'none', border: '1px solid var(--err)', borderRadius: 4, cursor: 'pointer' }}
            >🗑 Clear all</button>
          )}
        </h2>

        {/* Select all / deselect all + polygon list */}
        {(form.avoidPolygons?.length ?? 0) > 0 && (
          <>
            <div style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: 'var(--muted)', flex: 1 }}>
                {form.avoidPolygons.filter(p => p.active !== false).length}/{form.avoidPolygons.length} active
              </span>
              <button type="button" style={{ fontSize: 10, padding: '1px 6px' }} onClick={() => onToggleAllPolygons?.(true)}>✓ All</button>
              <button type="button" style={{ fontSize: 10, padding: '1px 6px' }} onClick={() => onToggleAllPolygons?.(false)}>✗ None</button>
            </div>

            <div className="polygon-list">
              {form.avoidPolygons.map((p, idx) => (
                <div key={p.id} className={`polygon-list-item${editingPolygonId === p.id ? ' editing' : ''}${p.active === false ? ' inactive' : ''}`}>
                  <input
                    type="checkbox"
                    checked={p.active !== false}
                    onChange={() => onTogglePolygonActive?.(p.id)}
                    title={p.active !== false ? 'Deactivate (keep but skip in route)' : 'Activate'}
                    style={{ flexShrink: 0, cursor: 'pointer' }}
                  />
                  <span className="polygon-num">#{idx + 1}</span>
                  <input
                    type="text"
                    className="polygon-label-input"
                    value={p.label}
                    onChange={(e) => set({
                      avoidPolygons: form.avoidPolygons.map(pp =>
                        pp.id === p.id ? { ...pp, label: e.target.value } : pp
                      ),
                    })}
                    onDoubleClick={() => onEditPolygon?.(p.id)}
                    title="Rename · Double-click to edit polygon on map"
                  />
                  <span className="polygon-pts">{p.vertices.length} pts</span>
                  <button type="button" onClick={() => onEditPolygon?.(p.id)} disabled={drawPolygonMode}
                    title="Edit polygon vertices on the map" className={editingPolygonId === p.id ? 'active' : ''}
                    style={{ fontSize: 10, padding: '2px 5px' }}>✏️</button>
                  <button type="button" onClick={() => onSavePolygonGlobal?.(p.id)}
                    title={`Save to global library (max ${MAX_GLOBAL_POLYGONS})`}
                    style={{ fontSize: 10, padding: '2px 5px' }}>💾</button>
                  <button type="button" onClick={() => onDeletePolygon?.(p.id)} title="Delete"
                    style={{ color: 'var(--err)', fontSize: 10, padding: '2px 5px' }}>🗑</button>
                </div>
              ))}
            </div>
          </>
        )}

        <button type="button" onClick={onStartDrawPolygon} disabled={drawPolygonMode}
          style={{ width: '100%', marginTop: (form.avoidPolygons?.length ?? 0) > 0 ? 6 : 0 }}
          title="Click points on the map to draw a new avoid area">
          {drawPolygonMode && !editingPolygonId ? '🔺 Drawing…' : '+ Add Polygon (Draw on Map)'}
        </button>

        {/* Global polygon library */}
        <details style={{ marginTop: 10 }}>
          <summary style={{ fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>
            🌐 Global Library ({globalPolygons.length}/{MAX_GLOBAL_POLYGONS})
            <span className="tip" style={{ marginLeft: 4 }} title="Polygons saved here persist in localStorage across sessions.">ⓘ</span>
          </summary>
          {globalPolygons.length === 0 ? (
            <p className="hint" style={{ marginTop: 4 }}>No saved polygons. Click 💾 on a polygon above to save it here.</p>
          ) : (
            <div className="polygon-list" style={{ marginTop: 6 }}>
              {globalPolygons.map((gp, idx) => (
                <div key={gp.id} className="polygon-list-item">
                  <span className="polygon-num">#{idx + 1}</span>
                  <span style={{ flex: 1, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} title={gp.label}>{gp.label}</span>
                  <span className="polygon-pts">{gp.vertices.length} pts</span>
                  <button type="button" onClick={() => onAddGlobalToRoute?.(gp)}
                    title="Add to current route's polygon list" style={{ fontSize: 10, padding: '2px 6px' }}>+ Use</button>
                  <button type="button" onClick={() => onDeleteGlobalPolygon?.(gp.id)}
                    title="Remove from global library" style={{ color: 'var(--err)', fontSize: 10, padding: '2px 5px' }}>🗑</button>
                </div>
              ))}
            </div>
          )}
        </details>

        {/* Manual JSON fallback */}
        <details style={{ marginTop: 6 }}>
          <summary style={{ fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>
            Manual GeoJSON override (used only when no polygons drawn above)
          </summary>
          <textarea rows={4}
            style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, width: '100%', marginTop: 4 }}
            placeholder={'{\n  "type": "Polygon",\n  "coordinates": [[[12.0,49.0],[12.1,49.0],[12.1,49.1],[12.0,49.0]]]\n}'}
            value={form.avoidPolygonsJson}
            onChange={(e) => set({ avoidPolygonsJson: e.target.value })}
          />
        </details>
        <p className="hint">Double-click a polygon on the map or its name to edit. Right-click vertex to remove. Drag to move.</p>
      </section>

      <section>
        <h2>Alternative Routes</h2>
        <label className="checkbox" title="Compute multiple route options.">
          <input
            type="checkbox"
            checked={form.altEnable}
            onChange={(e) => set({ altEnable: e.target.checked })}
          /> enable alternatives
        </label>
        <div className="grid2">
          <label>target_count
            <input type="number" min={2} max={3}
              disabled={!form.altEnable}
              value={form.altCount}
              onChange={(e) => set({ altCount: parseInt(e.target.value, 10) || 2 })}
            />
          </label>
          <label>share_factor
            <input type="number" step={0.05}
              disabled={!form.altEnable}
              value={form.altShare}
              onChange={(e) => set({ altShare: parseFloat(e.target.value) || 0.6 })}
            />
          </label>
          <label>weight_factor
            <input type="number" step={0.1}
              disabled={!form.altEnable}
              value={form.altWeight}
              onChange={(e) => set({ altWeight: parseFloat(e.target.value) || 1.4 })}
            />
          </label>
        </div>
      </section>
    </>
  );
}
